import React from "react";

import { DivPx, Input, Select, SelectOption } from "@moai/core";
import { useStorage } from "../../storage/use-storage";
import s from "./indent.module.scss";

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

export interface Indent {
    kind: string;
    width: number | null;
    custom: number;
}

export interface SetIndent {
    kind: SetState<string>;
    width: SetState<number | null>;
    custom: SetState<number>;
}

export const getIndentString = (indent: Indent) => {
    if (indent.kind === "tab") return "\t";
    return " ".repeat(indent.width ?? indent.custom);
};

export interface IndentState {
    indent: Indent;
    setIndent: SetIndent;
}

export const useIndent = (): IndentState => {
    const [kind, setKind] = useStorage("pref-indent-type", "tab");
    const [width, setWidth] = useStorage<number | null>("pref-indent-width", 4);
    const [custom, setCustom] = useStorage("pref-indent-custom", 3);

    return {
        indent: { kind, width, custom },
        setIndent: { kind: setKind, width: setWidth, custom: setCustom },
    };
};

interface Props extends IndentState {
    fullWidth?: boolean;
}

const kindOptions: SelectOption<string>[] = [
    { value: "tab", label: "Tabs" },
    { value: "space", label: "Spaces" },
];

const widthOptions: SelectOption<number | null>[] = [
    { value: 2, label: "2 spaces" },
    { value: 4, label: "4 spaces" },
    { value: 8, label: "8 spaces" },
    { value: null, label: "Custom" },
];

export const IndentSelect: React.FC<Props> = ({
    fullWidth,
    indent,
    setIndent,
}) => (
    <div className={`${s.container} ${fullWidth ? s.full : s.auto}`}>
        <div className={s.kind}>
            <Select
                value={indent.kind}
                setValue={setIndent.kind}
                options={kindOptions}
                fullWidth
            />
        </div>
        <DivPx size={8} />
        <div className={s.width}>
            <Select
                value={indent.width}
                setValue={setIndent.width}
                options={widthOptions}
                fullWidth
            />
        </div>
        {indent.width === null && (
            <>
                <DivPx size={8} />
                <div className={s.custom}>
                    <Input
                        type="number"
                        value={indent.custom}
                        setValue={setIndent.custom}
                        fullWidth
                    />
                </div>
            </>
        )}
    </div>
);
